import { Dot } from "@lcc/ui";
import { useMemo, type ReactNode } from "react";
import { endpoints } from "../api/endpoints";
import { useApi, useConfig } from "../api/hooks";
import type { LocalizedText, StaffFireConfig } from "../api/types";
import { can } from "../auth/roles";
import { useFireContext } from "../context";
import { formatDateTime, shortId } from "../format/datetime";

/** The English text of a refdata label (the console is in English; Nyanja and Bemba are for citizens). */
export function text(label: LocalizedText | string | null | undefined): string {
  if (!label) return "";
  return typeof label === "string" ? label : label.en;
}

export interface ConfigNames {
  staff: StaffFireConfig | null;
  incidentType: (key: string | null | undefined) => string;
  capability: (key: string | null | undefined) => string;
  dismissReason: (key: string | null | undefined) => string;
  riskClass: (key: string | null | undefined) => string;
  occupancy: (key: string | null | undefined) => string;
  oosReason: (key: string | null | undefined) => string;
  cause: (key: string | null | undefined) => string;
  propertyType: (key: string | null | undefined) => string;
}

function namer(entries: Array<{ key: string; label: LocalizedText }> | undefined) {
  const m = new Map((entries ?? []).map((e) => [e.key, text(e.label)]));
  return (key: string | null | undefined) => (key ? (m.get(key) ?? key) : "—");
}

/** Labels of the configuration in force (FER's cached `fer_*` sets): keys are never shown raw when a label exists. */
export function useConfigNames(): ConfigNames {
  const cfg = useConfig();
  return useMemo(() => {
    const data = cfg.data;
    const staff = data && data.view === "staff" ? data : null;
    return {
      staff,
      incidentType: namer(staff?.incident_types),
      capability: namer(staff?.capabilities),
      dismissReason: namer(staff?.dismiss_reasons),
      riskClass: namer(data?.risk_classes),
      occupancy: namer(data?.occupancy_types),
      oosReason: namer(staff?.out_of_service_reasons),
      cause: namer(staff?.causes),
      propertyType: namer(staff?.property_types),
    };
  }, [cfg.data]);
}

/** Ward names from FER's cached ward polygons (ids for those who may not read the ward map). */
export function useWardNames(): (wardId: string | null | undefined) => string {
  const { principal } = useFireContext();
  const q = useApi(["wards"], endpoints.getWardMap, { enabled: can(principal, "viewWardMap"), staleTime: 10 * 60_000 });
  return useMemo(() => {
    const m = new Map((q.data?.features ?? []).map((f) => [f.properties.ward_id, f.properties.name]));
    return (wardId: string | null | undefined) => (wardId ? (m.get(wardId) ?? wardId) : "Outside every ward");
  }, [q.data]);
}

/** Station names by id. */
export function useStationNames(): (stationId: string | null | undefined) => string {
  const { principal } = useFireContext();
  const q = useApi(["stations", {}], (api) => endpoints.listStations(api), { enabled: can(principal, "viewStations"), staleTime: 60_000 });
  return useMemo(() => {
    const m = new Map((q.data ?? []).map((s) => [s.station_id, `${s.name} (${s.code})`]));
    return (id: string | null | undefined) => (id ? (m.get(id) ?? shortId(id)) : "—");
  }, [q.data]);
}

/** "you" or "staff 1234abcd" for a party id the API does not name. */
export function personLabel(partyId: string | null | undefined, me: string, name?: string | null): string {
  if (!partyId) return "the system";
  if (partyId === me) return "you";
  return name ? name : `staff ${shortId(partyId)}`;
}

export function Row({ term, children }: { term: string; children: ReactNode }) {
  return (
    <>
      <dt>{term}</dt>
      <dd>{children}</dd>
    </>
  );
}

/** Screen heading with an optional live indicator. */
export function ScreenHeader({ title, live, children }: { title: string; live?: string | null; children?: ReactNode }) {
  return (
    <div className="fer-screen__header">
      <h2>{title}</h2>
      {live && (
        <span className="fer-live" role="status">
          <Dot tone="success" /> Updated {formatDateTime(live)}
        </span>
      )}
      {children}
    </div>
  );
}

/** A record's position as "lon, lat" with 5 decimals (about a metre). */
export function lonLat(lon: number | null | undefined, lat: number | null | undefined): string {
  if (typeof lon !== "number" || typeof lat !== "number") return "—";
  return `${lon.toFixed(5)}, ${lat.toFixed(5)}`;
}

/** Metres in words ("35 m", "1.2 km"). */
export function metres(m: number | null | undefined): string {
  if (m == null || !Number.isFinite(m)) return "unknown";
  return m < 1000 ? `${Math.round(m)} m` : `${(Math.round(m / 100) / 10).toFixed(1)} km`;
}

/** A required text of at least 3 characters (the contract's reasons and notes). */
export function reasonError(value: string, what = "A reason"): string | null {
  return value.trim().length >= 3 ? null : `${what} of at least 3 characters is required.`;
}

/** Full names of the regulators that enforce the standards checklist items name (ADR 0016 decision 46). */
export const REGULATOR_NAMES: Record<string, string> = {
  ZCSA: "Zambia Compulsory Standards Agency",
};

export type ChecklistItemInfo = StaffFireConfig["checklists"][number]["items"][number];

/** A checklist item by its code: from the given checklist, else from any checklist (codes are shared across lists). */
export function checklistItem(staff: StaffFireConfig | null, code: string, checklistKey?: string | null): ChecklistItemInfo | undefined {
  const lists = staff?.checklists ?? [];
  const own = checklistKey ? lists.find((c) => c.key === checklistKey)?.items.find((i) => i.code === code) : undefined;
  return own ?? lists.flatMap((c) => c.items).find((i) => i.code === code);
}

/**
 * The compulsory standard a checklist item checks, in words: "Standard: ZS 373:2010 (ZCSA, Zambia Compulsory Standards
 * Agency)". Nothing when the item names no standard.
 */
export function ItemStandard({ item }: { item: Pick<ChecklistItemInfo, "standard" | "regulator"> | undefined }) {
  if (!item?.standard) return null;
  const reg = item.regulator ?? null;
  const full = reg ? REGULATOR_NAMES[reg] : undefined;
  return (
    <span className="fer-standard">
      {" "}
      Standard: {item.standard}
      {reg && (
        <>
          {" "}
          (<abbr title={full ?? reg}>{reg}</abbr>
          {full ? `, ${full}` : ""})
        </>
      )}
    </span>
  );
}

/** The checklist items of an occupancy type's checklist that name a compulsory standard (certificates, applications). */
export function StandardsChecked({ staff, occupancyType }: { staff: StaffFireConfig | null; occupancyType: string }) {
  const key = staff?.occupancy_types.find((o) => o.key === occupancyType)?.checklist;
  const items = (staff?.checklists.find((c) => c.key === key)?.items ?? []).filter((i) => i.standard);
  if (items.length === 0) return null;
  return (
    <Row term="Compulsory standards checked">
      <ul className="fer-plain-list" aria-label="Compulsory standards checked">
        {items.map((i) => (
          <li key={i.code}>
            {i.label}
            {i.critical ? " (critical)" : ""}
            <ItemStandard item={i} />
          </li>
        ))}
      </ul>
    </Row>
  );
}
